import { compact, date, eq, expandIf } from '../../../../helpers/compact';
import { DOC_CLASS_CATEGORIES } from '../../../../helpers/pushs';
import { LinkMedia, LinkWeb } from '../../../../types';
import { DocumentType } from '../../../../types/document';
import { CLASSIFICATION, FILTER } from '../../../../types/filters/index';
import { MEDIA_TYPE, MediaPress } from '../../../../types/press-media';
import { PressRelease } from '../../../../types/press-release';
import { toLink } from '../../fields/link';
import { toText } from '../../fields/text';
import {
  regionTypeDefault,
  toMediaPrismic,
  toMediaType,
  toPublicationType,
  toRegionType,
  toThematicType,
} from '../../mapping/index';
import { toRegionFilterType } from '../../mapping/region';
import { image } from '../../partials/image';
import { deprecatedPageEntity, documentEntity } from './internal';

const isMediaOfType = (mediaType: string, category: string) =>
  category === toMediaPrismic(mediaType);

const processPressRelease = (entity: any): PressRelease =>
  compact({
    type: DocumentType.PRESS_RELEASE,
    title: toText(entity?.data?.title),
    description: toText(entity?.data?.description),
    image: image(entity?.data),
    localization:
      toRegionType(entity?.data?.localization) || regionTypeDefault(),
    related_article: toLink(entity?.data?.related_article),
    publication_date: date(entity?.data?.publication_date, 'YYYY-MM-DD'),
    release_date: date(entity?.data?.date, 'YYYY-MM-DD'),
    related_date: date(entity?.data?.publication_date, 'YYYY-MM-DD'),
    link: toLink(entity?.data?.uri),
    onFinance: eq('com_visible_finance', 'oui', false, entity?.data),
    onNewsroom: eq('com_visible_newsroom', 'oui', true, entity?.data),
    thematic: toThematicType(entity?.data?.mediaThematic),
    filters: compact({
      [FILTER.REGION]:
        toRegionFilterType(entity?.data?.localization) ||
        toRegionFilterType(entity?.data?.national) ||
        toRegionFilterType(entity?.data?.international) ||
        regionTypeDefault(),
      [FILTER.THEMATIC]: toThematicType(entity?.data?.mediaThematic),
    }),
  });

const processMediaPress = (entity: any): MediaPress => {
  const normalizedImage = image(entity?.data);
  const normalizedLink = toLink(entity?.data?.uri) as LinkMedia | LinkWeb;
  const publicationType = entity?.data?.publication || entity?.data?.pub_type;
  const onFinance =
    isMediaOfType(MEDIA_TYPE.PUBLICATION, entity?.data.category) &&
    eq('pub_visible_finance', 'oui', true, entity?.data);

  return compact({
    type: DocumentType.MEDIA_PRESS,
    description: toText(entity?.data?.description),
    publication: toPublicationType(publicationType),
    mediaType: toMediaType(entity?.data?.category),
    thematic: toThematicType(entity?.data?.thematic),
    localization:
      toRegionType(entity?.data?.localization) ||
      toRegionType(entity?.data?.national) ||
      toRegionType(entity?.data?.international) ||
      regionTypeDefault(),
    image: normalizedImage,
    link: normalizedLink,
    downloadUrl: normalizedLink?.url || normalizedImage?.src,
    related_article: toLink(entity?.data?.related_article),
    onNewsroom: true,

    ...expandIf(isMediaOfType(MEDIA_TYPE.VIDEO, entity?.data.category), () => ({
      transcription: entity?.data?.video_transcription,
    })),
    ...expandIf(isMediaOfType(MEDIA_TYPE.AUDIO, entity?.data.category), () => ({
      transcription: entity?.data?.audio_transcription,
    })),
    ...expandIf(
      isMediaOfType(MEDIA_TYPE.PUBLICATION, entity?.data.category),
      () => ({
        onNewsroom: false,
        classCategory: DOC_CLASS_CATEGORIES.PUBLICATION,
        onHome: eq('pub_visible_home', 'oui', true, entity?.data),
        onFinance,
        date: date(entity?.data?.pub_date, 'YYYY-MM-DD'),
        relatedDate: date(entity?.data?.pub_related_date, 'YYYY-MM-DD'),
      })
    ),
    // in filters, there might be redundant data but it's necessary for the search engine.
    filters: compact({
      [FILTER.MEDIA_TYPE]: toMediaType(entity?.data?.category),
      [FILTER.REGION]:
        toRegionFilterType(entity?.data?.localization) ||
        toRegionFilterType(entity?.data?.national) ||
        toRegionFilterType(entity?.data?.international) ||
        regionTypeDefault(),
      [FILTER.THEMATIC]: toThematicType(entity?.data?.thematic),
      [FILTER.MEDIA_THEMATIC]: toThematicType(entity?.data?.mediaThematic),
      [FILTER.COMMON_THEMATIC]:
        toThematicType(entity?.data?.mediaThematic) ||
        toThematicType(entity?.data?.thematic),
      [FILTER.CLASSIFICATION]: isMediaOfType(
        MEDIA_TYPE.PUBLICATION,
        entity?.data.category
      )
        ? CLASSIFICATION.PUBLICATION
        : null,
      [FILTER.PUBLICATION]:
        !onFinance &&
        isMediaOfType(MEDIA_TYPE.PUBLICATION, entity?.data.category)
          ? toPublicationType(publicationType)
          : null,
      [FILTER.FINANCIAL_PUBLICATION]: onFinance
        ? toPublicationType(publicationType)
        : null,
    }),
    deprecated: [
      entity?.data?.thematic &&
        !toThematicType(entity?.data?.thematic) && {
          field: FILTER.THEMATIC,
          oldValue: entity?.data?.thematic,
        },
    ].filter(Boolean),
  });
};

export const MediaPressEntity = deprecatedPageEntity<MediaPress>(
  DocumentType.MEDIA_PRESS,
  {
    processStrategy: processMediaPress,
  }
);

export const MediaPressReleaseEntity = documentEntity<PressRelease>(
  DocumentType.PRESS_RELEASE,
  {
    processStrategy: processPressRelease,
  }
);
